import React, { useState } from "react";
import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import generateExcel from "../../helpers/generateExcel";
import Loader from "../../pages/pages/loader";

/**
 * DownloadAllDataButton Component
 * ------------------------------------
 * Props:
 * - lazyQueryHook: RTK lazy query hook (e.g., useLazyListCampaignQuery)
 * - queryParams: optional query parameters
 * - fileName: base name for the downloaded Excel file
 * - transformData: optional mapping object for data transformation
 *   Example:
 *   {
 *     customer: (item) => item.customer?.customer_name,
 *     country: (item) => item.country?.country_name,
 *     campaign_status: (item) => (item.campaign_status ? "Active" : "Inactive")
 *   }
 */
const DownloadAllDataButton = ({
  lazyQueryHook,
  queryParams = {},
  fileName = "data",
  transformData = {}, // 👈 mapping of only fields that need transformation
}) => {
  const [trigger, { isFetching }] = lazyQueryHook();
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    try {
      setLoading(true);

      // Fetch all data manually
      const result = await trigger({
        ...queryParams,
        page: "*",
        page_size: undefined,
      }).unwrap();

      let allData = result?.results || result || [];

      if (!allData.length) {
        message.warning("No data available to export.");
        return;
      }

      // ✅ Apply mapping-based transformation
      if (transformData && Object.keys(transformData).length > 0) {
        allData = allData.map((item) => {
          const transformedItem = { ...item }; // start with full original item
          for (const key in transformData) {
            const transform = transformData[key];
            try {
              transformedItem[key] =
                typeof transform === "function" ? transform(item) : item[key];
            } catch (err) {
              console.warn(`Transformation error for key "${key}"`, err);
            }
          }
          return transformedItem;
        });
      }

      const today = new Date().toISOString().split("T")[0];
      const fileWithDate = `${fileName}_${today}`;

      generateExcel(allData, fileWithDate);
      message.success(`Excel file "${fileWithDate}.xlsx" downloaded successfully!`);
    } catch (error) {
      console.error("Excel download error:", error);
      message.error("Failed to download data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Loader isLoading={loading || isFetching} />
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        loading={loading || isFetching}
        onClick={handleDownload}
      >
        Download All
      </Button>
    </>
  );
};

export default DownloadAllDataButton;
